import { useRef, useState } from 'react'
import { X, Percent, DollarSign, Tag, Loader2 } from 'lucide-react'
import { useDiscountReasons } from '@/api/receipts'
import { usePOSStore, isReceiptPartiallyPaid } from '@/stores/posStore'
import { useCartStore } from '@/stores/cartStore'
import { useUIStore } from '@/stores/uiStore'
import { usePOSConfig } from '@/api/config'
import type { Cart, CartDiscount } from '@/lib/cart'
import { ManagerApprovalDialog } from '@/components/functions/ManagerApprovalDialog'
import {
  REMOVE_CART_DISCOUNT_FIRST,
  allApproved,
  approvalRows,
  approveDiscount,
  discardApprovals,
  cartSnapshot,
  currentEligibleBase,
  probeDiscount,
  useDiscountLimit,
} from '@/hooks/useDiscountApproval'
import {
  approvalSubtitle,
  cartWithLineDiscounts,
  combinedDropExceedsLimit,
  exceedsLimit,
  lineApprovalItem,
  receiptApprovalItem,
  type ApprovalItem,
} from '@/lib/discountLimit'

/** A validated discount, ready to apply (see handleApply). */
type PendingDiscount =
  | { scope: 'receipt'; params: CartDiscount }
  | {
      scope: 'line'
      lineDiscount: number
      percent: number
      reasonId: number | null
      reasonOther: string | null
    }

type DiscountMode = 'percent' | 'amount' | 'coupon'

/** What the manager is asked to approve for a pending discount: the cart as
 *  it will be, and every value on it the PIN covers (listed in the dialog). */
interface ApprovalPlan {
  discount: PendingDiscount
  nextCart: Cart
  items: ApprovalItem[]
}

export function DiscountModal() {
  const closeModal = useUIStore((s) => s.closeModal)
  const showToast = useUIStore((s) => s.showToast)
  const modalData = useUIStore((s) => s.modalData)
  const cart = usePOSStore((s) => s.cart)
  const serverTotals = usePOSStore((s) => s.serverTotals)
  const isRefundMode = usePOSStore((s) => s.isRefundMode)
  const setDiscount = useCartStore((s) => s.setDiscount)
  const setLineDiscount = useCartStore((s) => s.setLineDiscount)
  const previewItems = useCartStore((s) => s.preview?.items ?? null)
  const activeCartDiscount = useCartStore((s) => s.discount)
  // The base a receipt $ discount is judged on (the last preview's eligible
  // subtotal). Re-read on every preview.
  const eligibleBase = useCartStore((s) => currentEligibleBase({ preview: s.preview, discount: s.discount }))
  const currentReceiptId = usePOSStore((s) => s.currentReceiptId)
  const updateCartItem = usePOSStore((s) => s.updateCartItem)
  const { limit, label: limitLabel } = useDiscountLimit()
  // Validated discount awaiting a manager PIN (ManagerApprovalDialog).
  const [awaitingApproval, setAwaitingApproval] = useState<ApprovalPlan | null>(null)
  // True only while the approval dialog is open: an approval that resolves
  // after the dialog was closed (possible while offline) must not apply.
  const approvalOpenRef = useRef(false)
  // A recalled receipt is being asked whether it already carries an
  // approval for this value.
  const [probing, setProbing] = useState(false)
  const { data: posConfig } = usePOSConfig()

  // Lock discount application on a partially-paid receipt. Both
  // cartStore.setDiscount and setLineDiscount silently no-op in this
  // state (the data-safety backstop), but without surfacing it here
  // the modal would fire its success toast and close as if the
  // discount applied. Disables the Apply button preemptively; the
  // inline error in handleApply catches the Enter-key path that
  // bypasses the disabled state.
  const partialPaymentLocked = isReceiptPartiallyPaid(serverTotals, isRefundMode)

  // When `targetProductId` is in modalData, we're scoped to a single line
  // (legacy discount_item). Otherwise the modal applies the discount to the
  // whole cart (legacy discount_receipt). The two scopes are mutually
  // exclusive on the wire — see cartToRequest's resolution rule.
  const targetProductId = modalData?.targetProductId as number | undefined
  const targetLotNumber = (modalData?.targetLotNumber as string | null | undefined) ?? null
  const isLineScope = targetProductId !== undefined
  const targetLine = isLineScope
    ? cart.find((c) => c.product_id === targetProductId && (c.lot_number ?? null) === targetLotNumber)
    : null
  // Match the exact priced row for this line -- the same SKU can sit on
  // multiple lots at different prices. The row's lot lives in
  // `batch_number` on the wire (`lot_number` only on legacy responses). The
  // product_id-only fallback is used ONLY when it's unambiguous (exactly one
  // priced row for the SKU); with multiple lots we can't tell which row, so
  // fall through to the catalog price rather than guess wrong.
  const pricedRow = (() => {
    if (!isLineScope) return undefined
    const sameSku = previewItems?.filter((it) => it.product_id === targetProductId) ?? []
    return (
      sameSku.find((it) => ((it.batch_number ?? it.lot_number) ?? null) === targetLotNumber)
      ?? (sameSku.length === 1 ? sameSku[0] : undefined)
    ) as { price?: string | number; subtotal?: string | number } | undefined
  })()
  const targetLineQty = targetLine != null ? Math.abs(targetLine.quantity) : 0
  // The line's gross (before the cashier's discount), SERVER-priced when a
  // preview has priced it: posStore `unit_price` is the catalog price, which
  // is not what the gate measures a member-priced line against.
  const serverUnit = pricedRow ? parseFloat(String(pricedRow.price ?? '')) : NaN
  const targetLineSubtotal =
    targetLine != null
      ? (Number.isFinite(serverUnit) && serverUnit > 0
        ? serverUnit
        : typeof targetLine.unit_price === 'number'
          ? targetLine.unit_price
          : parseFloat(String(targetLine.unit_price)) || 0) * targetLineQty
      : 0

  const [mode, setMode] = useState<DiscountMode>('percent')
  const [percent, setPercent] = useState('')
  const [amount, setAmount] = useState('')
  const [coupon, setCoupon] = useState('')
  const [reasonId, setReasonId] = useState<number | null>(null)
  const [reasonOther, setReasonOther] = useState('')
  const [error, setError] = useState('')

  const { data: reasons = [], isLoading: reasonsLoading } = useDiscountReasons()

  const validate = (): PendingDiscount | null => {
    setError('')
    if (partialPaymentLocked) {
      setError('Receipt has payment(s) applied — void the payment in Checkout before applying a discount.')
      return null
    }
    if (cart.length === 0) {
      setError('Add items to the cart before applying a discount.')
      return null
    }

    // Coupon mode is receipt-level only (server resolves the coupon
    // against the whole cart). Block it when the modal is scoped to a
    // single line.
    if (isLineScope && mode === 'coupon') {
      setError('Coupons apply to the whole cart, not a single item.')
      return null
    }

    if (isLineScope) {
      // Per-line discount: BE wire shape is dollars-only, so convert
      // percent → amount against the line's subtotal before storing.
      let lineDiscount = 0
      if (mode === 'percent') {
        const val = parseFloat(percent)
        if (!val || val <= 0 || val > 100) { setError('Enter a valid percent between 1 and 100.'); return null }
        lineDiscount = +(targetLineSubtotal * val / 100).toFixed(2)
      } else {
        const val = parseFloat(amount)
        if (!val || val <= 0) { setError('Enter a valid discount amount.'); return null }
        if (val > targetLineSubtotal) {
          setError(`Discount can't exceed the line subtotal ($${targetLineSubtotal.toFixed(2)}).`)
          return null
        }
        lineDiscount = val
      }

      // Below-cost guard (pos.disallow_price_lower_than_cost, default on):
      // a manual line discount may not price the line under cost. Mirrors
      // ProductModal; refund lines are exempt. Check against the
      // tier/preview-priced subtotal -- posStore `unit_price` is the stale
      // catalog price (see CartItemRow), which would let a member-priced
      // line slip below cost. Recover the gross by adding back any existing
      // line discount (same as ProductModal's pricedFromCart).
      if (targetLine && !isRefundMode) {
        const unitCost = Number(targetLine.product?.cost ?? 0)
        const lineQty = Math.abs(targetLine.quantity)
        // `pricedRow`: this line's priced row (see its declaration above).
        const pricedSub = pricedRow ? parseFloat(String(pricedRow.subtotal ?? '')) : NaN
        const grossSubtotal = Number.isFinite(pricedSub)
          ? pricedSub + Math.abs(Number(targetLine.discount_amount ?? 0))
          : targetLineSubtotal
        if (
          posConfig?.posDisallowPriceLowerThanCost !== false &&
          unitCost > 0 &&
          (grossSubtotal - lineDiscount) < unitCost * lineQty
        ) {
          setError(`This discount prices the item below cost ($${unitCost.toFixed(2)}). Reduce the discount.`)
          return null
        }
      }

      let resolvedReasonId: number | undefined
      let resolvedReasonOther: string | undefined
      if (reasonId != null && reasonId !== -1) resolvedReasonId = reasonId
      if (reasonId === -1) {
        if (!reasonOther.trim()) { setError('Enter a reason description.'); return null }
        resolvedReasonOther = reasonOther.trim()
      }

      return {
        scope: 'line',
        lineDiscount,
        // The entered percent when there is one (rounding the dollar figure
        // could nudge it over the limit); else the dollar share of the line.
        percent: mode === 'percent'
          ? parseFloat(percent)
          : targetLineSubtotal > 0 ? +((lineDiscount / targetLineSubtotal) * 100).toFixed(2) : 0,
        reasonId: resolvedReasonId ?? null,
        reasonOther: resolvedReasonOther ?? null,
      }
    }

    // Cart-level (receipt-wide) discount.
    const params: CartDiscount = {}

    if (mode === 'coupon') {
      if (!coupon.trim()) { setError('Enter a coupon code.'); return null }
      params.coupon = coupon.trim()
    } else if (mode === 'percent') {
      const val = parseFloat(percent)
      if (!val || val <= 0 || val > 100) { setError('Enter a valid percent between 1 and 100.'); return null }
      params.percent = val
    } else {
      const val = parseFloat(amount)
      if (!val || val <= 0) { setError('Enter a valid discount amount.'); return null }
      params.amount = val
    }

    if (mode !== 'coupon') {
      if (reasonId != null && reasonId !== -1) params.reason_id = reasonId
      if (reasonId === -1) {
        if (!reasonOther.trim()) { setError('Enter a reason description.'); return null }
        params.reason_other = reasonOther.trim()
      }
    }

    return { scope: 'receipt', params }
  }

  // Units in the receipt's eligible base: the half-cent-per-unit tolerance
  // of a receipt $ discount. Same lines as the server's base: discount-exempt
  // and vape-tax products are out.
  const cartUnits = cart.reduce((n, c) => {
    const p = c.product as { discount_exempt?: boolean | null; is_vape_tax?: boolean | null } | null
    return p?.discount_exempt || p?.is_vape_tax ? n : n + Math.abs(c.quantity)
  }, 0)
  // A manual price on the target line (not a gift card's balance) is judged
  // with the discount as one drop from list, like the server.
  const targetManualPrice = targetLine?.manual_price === true && !targetLine.gift_card_number
    ? targetLine.unit_price
    : null
  const lineOverLimit = (lineDollars: number): boolean => combinedDropExceedsLimit({
    list: targetLineQty > 0 ? targetLineSubtotal / targetLineQty : 0,
    price: targetManualPrice,
    unitDiscount: targetLineQty > 0 ? lineDollars / targetLineQty : 0,
    qty: targetLineQty,
  }, limit)

  // Over the clerk's limit? The server's rule: a percent compares directly;
  // dollars against the eligible base with half a cent per unit -- a line's
  // $ (what goes on the wire, even when entered as a percent) against its
  // server-priced gross, a receipt $ against the eligible subtotal. Coupons
  // and refund mode are never gated here.
  const needsApprovalFor = (d: PendingDiscount): boolean => {
    if (isRefundMode) return false
    if (d.scope === 'line') return lineOverLimit(d.lineDiscount)
    if (d.params.percent != null) return exceedsLimit('percent', d.params.percent, null, limit)
    if (d.params.amount != null) return exceedsLimit('amount', d.params.amount, eligibleBase, limit, cartUnits)
    return false
  }

  /** The requested percent, for the dialog's "N% is over your limit". */
  const requestedPercentOf = (d: PendingDiscount): number | null => {
    if (d.scope === 'line') return d.percent
    if (d.params.percent != null) return d.params.percent
    return d.params.amount != null && eligibleBase != null && eligibleBase > 0
      ? (d.params.amount / eligibleBase) * 100
      : null
  }

  /** The cart as `d` will leave it and the values the PIN must cover. */
  const planFor = (d: PendingDiscount): ApprovalPlan => {
    const snapshot = cartSnapshot()
    if (d.scope === 'receipt') {
      const nextCart = { ...snapshot, discount: d.params }
      const item = receiptApprovalItem(nextCart, eligibleBase)
      return { discount: d, nextCart, items: item ? [item] : [] }
    }
    const nextCart = cartWithLineDiscounts(snapshot, [{
      product_id: targetProductId!,
      lot_number: targetLotNumber,
      qty: targetLine?.quantity ?? 1,
      discount_amount: d.lineDiscount,
      discount_reason_id: d.reasonId,
      discount_reason_other: d.reasonOther,
    }])
    const line = nextCart.lines.find(
      (l) => l.product_id === targetProductId && (l.lot_number ?? null) === targetLotNumber,
    )
    const item = line ? lineApprovalItem(line) : null
    return { discount: d, nextCart, items: item ? [item] : [] }
  }

  const applyDiscount = (d: PendingDiscount) => {
    if (d.scope === 'line') {
      setLineDiscount(targetProductId!, targetLotNumber, {
        discount_amount: d.lineDiscount,
        reason_id: d.reasonId,
        reason_other: d.reasonOther,
      })
      // posStore is the bridge's source of truth for line discounts: without
      // this the next posStore change re-mirrors the line WITHOUT the
      // discount (and its reason), silently dropping it.
      if (targetLine) {
        updateCartItem(targetLine.id, {
          discount_amount: d.lineDiscount,
          discount_percent: d.percent,
          discount_reason_id: d.reasonId,
          discount_reason_other: d.reasonOther,
        })
      }
      closeModal()
      showToast({ type: 'success', title: 'Item Discount Applied' })
      return
    }
    setDiscount(d.params)
    closeModal()
    showToast({ type: 'success', title: 'Discount Applied', message: 'Cart totals will refresh momentarily.' })
  }

  const handleApply = async () => {
    if (probing) return
    const d = validate()
    if (!d) return
    if (!needsApprovalFor(d)) {
      applyDiscount(d)
      return
    }
    // An item discount is off the wire while a cart discount is on, so no
    // PIN could approve it: say so now, not after the manager typed a PIN.
    if (d.scope === 'line' && activeCartDiscount) {
      setError(REMOVE_CART_DISCOUNT_FIRST)
      return
    }
    const plan = planFor(d)
    // Already approved at exactly this tuple (re-applying it, e.g. to change
    // the reason): the riding approval covers it.
    if (allApproved(plan.items)) {
      applyDiscount(d)
      return
    }
    // A recalled/unheld receipt may already carry an approval for this value
    // (the server reuses it): let the server decide, and only ask for a PIN
    // on its 409. Otherwise ask up front.
    if (currentReceiptId != null) {
      setProbing(true)
      let verdict: Awaited<ReturnType<typeof probeDiscount>>
      try {
        verdict = await probeDiscount(plan.nextCart)
      } finally {
        setProbing(false)
      }
      if (verdict !== 'needs_approval') {
        applyDiscount(d)
        return
      }
    }
    approvalOpenRef.current = true
    setAwaitingApproval(plan)
  }

  // Manager PIN entered: price the cart as it will be, with the PIN on
  // exactly the listed values, then apply. A refusal rejects and the dialog
  // shows the reason.
  const approveAndApply = async (pin: string) => {
    const plan = awaitingApproval
    if (!plan) throw new Error('Nothing to approve.')
    const { recorded } = await approveDiscount(plan.nextCart, plan.items, pin)
    // Closed while the check was in flight (offline): don't apply behind
    // the cashier's back, and drop what it recorded.
    if (!approvalOpenRef.current) {
      discardApprovals(recorded)
      return
    }
    approvalOpenRef.current = false
    setAwaitingApproval(null)
    applyDiscount(plan.discount)
  }

  const canApply =
    mode === 'coupon' ? !!coupon.trim() :
    mode === 'percent' ? parseFloat(percent) > 0 :
    parseFloat(amount) > 0

  // Live pre-check for the button label (the real check runs on Apply, and
  // the backend has the last word).
  const entryNeedsApproval = (() => {
    if (isRefundMode || mode === 'coupon') return false
    if (isLineScope) {
      // The line's $ as validate() will compute it.
      const dollars = mode === 'percent'
        ? +(targetLineSubtotal * (parseFloat(percent) || 0) / 100).toFixed(2)
        : parseFloat(amount)
      return Number.isFinite(dollars) && dollars > 0 && lineOverLimit(dollars)
    }
    if (mode === 'percent') return exceedsLimit('percent', parseFloat(percent), null, limit)
    return exceedsLimit('amount', parseFloat(amount), eligibleBase, limit, cartUnits)
  })()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col animate-scale-in">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isLineScope ? 'Discount Item' : 'Discount Receipt'}
            </h2>
            {isLineScope && targetLine?.product && (
              <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">
                {targetLine.product.name ?? targetLine.product.display_name ?? `Item #${targetProductId}`}
                <span className="ml-1 text-gray-400">· line subtotal ${targetLineSubtotal.toFixed(2)}</span>
              </p>
            )}
          </div>
          <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Partial-payment lock banner. Pre-empts the fill-then-fail
              flow: cashier sees the lock the moment the modal opens
              rather than discovering it after entering a percent/amount
              and clicking Apply. Pairs with the disabled Apply button
              and the inline error in handleApply (Enter-key path). */}
          {partialPaymentLocked && (
            <div className="text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
              Receipt has payment(s) applied. Void the payment in Checkout to apply a discount.
            </div>
          )}

          {/* The clerk's own limit (hidden when unlimited, and in refund
              mode where no discount is entered). */}
          {limitLabel && !isRefundMode && (
            <p className="text-xs text-gray-500 -mb-1">{limitLabel}</p>
          )}

          {/* Mode Toggle. Coupon is hidden when scoped to a single line — */}
          {/* coupons resolve against the whole cart on the BE side. */}
          <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
            {([
              { key: 'percent', label: 'Percent', icon: Percent },
              { key: 'amount', label: 'Fixed $', icon: DollarSign },
              ...(isLineScope ? [] : [{ key: 'coupon' as const, label: 'Coupon', icon: Tag }]),
            ] as { key: DiscountMode; label: string; icon: typeof Percent }[]).map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => { setMode(key); setError('') }}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>

          {/* Input */}
          {mode === 'percent' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Discount Percent</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={percent}
                  onChange={(e) => { setPercent(e.target.value); setError('') }}
                  onKeyDown={(e) => e.key === 'Enter' && void handleApply()}
                  placeholder="e.g. 10"
                                    className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-0 outline-none disabled:bg-gray-50"
                  autoFocus
                />
                <Percent size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
          )}

          {mode === 'amount' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Discount Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                <input
                  type="number"
                  min="0"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError('') }}
                  onKeyDown={(e) => e.key === 'Enter' && void handleApply()}
                  placeholder="0.00"
                                    className="w-full pl-7 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-0 outline-none disabled:bg-gray-50"
                  autoFocus
                />
              </div>
            </div>
          )}

          {mode === 'coupon' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Coupon Code</label>
              <input
                type="text"
                value={coupon}
                onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setError('') }}
                onKeyDown={(e) => e.key === 'Enter' && void handleApply()}
                placeholder="Enter coupon code"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-0 outline-none tracking-widest font-mono disabled:bg-gray-50"
                autoFocus
              />
            </div>
          )}

          {/* Reason (not for coupons) */}
          {mode !== 'coupon' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Reason</label>
              {reasonsLoading ? (
                <div className="flex items-center gap-2 py-2 text-gray-400 text-sm">
                  <Loader2 size={14} className="animate-spin" /> Loading reasons...
                </div>
              ) : (
                <select
                  value={reasonId ?? ''}
                  onChange={(e) => { setReasonId(e.target.value ? Number(e.target.value) : null); setError('') }}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-0 outline-none bg-white disabled:bg-gray-50"
                >
                  <option value="">No reason selected</option>
                  {reasons.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}{r.percent ? ` (${r.percent}%)` : ''}</option>
                  ))}
                  <option value="-1">Other...</option>
                </select>
              )}
              {reasonId === -1 && (
                <input
                  type="text"
                  value={reasonOther}
                  onChange={(e) => { setReasonOther(e.target.value); setError('') }}
                  placeholder="Describe the reason"
                                    className="mt-2 w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-0 outline-none text-sm disabled:bg-gray-50"
                />
              )}
            </div>
          )}

          {error && (
            <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}

          <div className="flex gap-3 pt-1">
            <button
              onClick={closeModal}
              className="flex-1 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={() => void handleApply()}
              disabled={!canApply || partialPaymentLocked || probing}
              title={partialPaymentLocked
                ? 'Receipt has payment(s) applied — void the payment in Checkout before applying a discount'
                : undefined}
              className={`flex-1 py-3 ${entryNeedsApproval ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-500 hover:bg-green-600'} disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2`}
            >
              {probing
                ? <><Loader2 size={16} className="animate-spin" /> Checking approval…</>
                : entryNeedsApproval ? 'Needs manager approval' : 'Apply Discount'}
            </button>
          </div>
        </div>
      </div>
      <ManagerApprovalDialog
        isOpen={awaitingApproval !== null}
        onClose={() => {
          approvalOpenRef.current = false
          setAwaitingApproval(null)
        }}
        title="Approve discount"
        subtitle={approvalSubtitle(limit, awaitingApproval ? requestedPercentOf(awaitingApproval.discount) : null)}
        items={approvalRows(awaitingApproval?.items ?? [])}
        onSubmit={approveAndApply}
      />
    </div>
  )
}
